import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ServiceWorkerModule } from '@angular/service-worker';
import { inject, NgZone, NgModule, provideEnvironmentInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CDK_DRAG_CONFIG } from '@angular/cdk/drag-drop';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { MsalModule, MsalBroadcastService, MsalGuard, MsalInterceptor, MsalRedirectComponent, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';

import { akitaDevtools, DevtoolsOptions } from '@datorama/akita';

import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CmgSharedModule } from './shared';
import { CoreModule } from './core';
import { environment } from '../environments/environment';
import { UiModule } from '@ui';
import { SecurityProfileAuthTokenInterceptor } from './interceptors';
import { MessageModule } from 'primeng/message';
import { providePrimeNG } from 'primeng/config';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { Clear2PrimeNGTheme } from '@cmg-libs-ui/primeng-themes/clear2';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';

export function provideAkitaDevtools(options: Partial<DevtoolsOptions> = {}) {
  return provideEnvironmentInitializer(() => {
    const initializerFn = (() => {
      return () => {
        akitaDevtools(inject(NgZone), options);
      };
    })();
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    return initializerFn();
  });
}

const CdkDragDropConfig = {
  dragStartThreshold: 0,
  pointerDirectionChangeThreshold: 5,
  zIndex: 10000,
};

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    CoreModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    ButtonModule,
    CardModule,
    CmgSharedModule,
    DialogModule,
    SelectModule,
    ProgressBarModule,
    RadioButtonModule,
    ReactiveFormsModule,
    ToastModule,
    MessageModule,
    UiModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.serviceWorkerEnabled,
      registrationStrategy: 'registerWithDelay:' + environment.serviceWorkerRegistrationDelay,
    }),
    MsalModule.forRoot(
      new PublicClientApplication({
        auth: {
          clientId: environment.auth.clientId,
          authority: `https://login.microsoftonline.com/${environment.auth.tenantId}`,
          redirectUri: `${environment.baseUrl}/auth`,
          postLogoutRedirectUri: environment.baseUrl,
        },
        cache: {
          cacheLocation: BrowserCacheLocation.LocalStorage,
        },
        system: {
          loggerOptions: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-console
            loggerCallback: (level, message) => {
              // eslint-disable-next-line no-console
              // console.log(message);
            },
            // piiLoggingEnabled: true,
            logLevel: LogLevel.Info,
          },
        },
        telemetry: {
          application: {
            appName: 'CMG CLEAR 2 UI',
            appVersion: '',
          },
        },
      }),
      {
        // MSAL Guard Configuration
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: environment.auth.scopes,
        },
        loginFailedRoute: '/login-failed',
      },
      {
        // MSAL Interceptor Configuration
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map<string, string[]>([[environment.retail.baseUrl, environment.auth.scopes]]),
      },
    ),
  ],
  providers: [
    provideAkitaDevtools(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SecurityProfileAuthTokenInterceptor,
      multi: true,
    },
    {
      provide: CDK_DRAG_CONFIG,
      useValue: CdkDragDropConfig,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: APP_ENV, useValue: environment },
    { provide: 'environment', useValue: environment },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Clear2PrimeNGTheme,
        options: {
          darkModeSelector: false,
        },
      },
    }),
  ],
})
export class AppModule {}
